/**
 * Six boxes for a one-time code. Calls ``onComplete`` when the operator's
 * typing or paste fills the last box, so the code submits without a click.
 * It fires only on that input, never on re-render, because every wrong
 * code counts toward the account lockout.
 */
import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

const LENGTH = 6;

export function OtpInput({
  label,
  value,
  onChange,
  onComplete,
  disabled = false,
  readOnly = false,
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  onComplete: (code: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  /** Keep focus on the first box whenever the code is empty. */
  autoFocus?: boolean;
}) {
  const boxes = useRef<Array<HTMLInputElement | null>>([]);
  const empty = value === "";
  // focus() runs onFocus before React re-renders, so it can't read the prop.
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (autoFocus && empty && !disabled) boxes.current[0]?.focus();
  }, [autoFocus, empty, disabled]);

  function insert(at: number, raw: string) {
    if (readOnly) return;
    const digits = raw.replace(/\D/g, "");
    if (!digits) return;
    const next = (value.slice(0, at) + digits + value.slice(at + digits.length)).slice(0, LENGTH);
    latest.current = next;
    onChange(next);
    boxes.current[Math.min(at + digits.length, LENGTH - 1)]?.focus();
    if (next.length === LENGTH) onComplete(next);
  }

  function onKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (readOnly) return;
      const at = i < value.length ? i : i - 1;
      if (at < 0) return;
      latest.current = value.slice(0, at) + value.slice(at + 1);
      onChange(latest.current);
      boxes.current[at]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      boxes.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < value.length) {
      boxes.current[i + 1]?.focus();
    }
  }

  function onPaste(i: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    insert(i, e.clipboardData.getData("text"));
  }

  return (
    <div role="group" aria-label="6-digit code" className="flex justify-center gap-2 sm:gap-3">
      {Array.from({ length: LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            boxes.current[i] = el;
          }}
          aria-label={i === 0 ? label : `Digit ${i + 1} of ${LENGTH}`}
          inputMode="numeric"
          pattern="[0-9]*"
          // One box for the browser or password manager to fill; insert() spreads it.
          autoComplete={i === 0 ? "one-time-code" : "off"}
          value={value[i] ?? ""}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(e) => insert(i, e.target.value.slice(value[i] ? 1 : 0) || e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={(e) => onPaste(i, e)}
          // No gaps: a click past the first empty box lands on that box.
          onFocus={(e) => {
            if (i > latest.current.length) boxes.current[latest.current.length]?.focus();
            else e.target.select();
          }}
          className={cn(
            "h-12 w-10 rounded-lg border border-input bg-background text-center font-mono text-xl font-semibold shadow-sm transition-colors sm:h-14 sm:w-12",
            "focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
      ))}
    </div>
  );
}
